import { PublicKey, Connection, Keypair, Transaction, TransactionInstruction, SystemProgram } from '@solana/web3.js';
import * as anchor from '@project-serum/anchor';
import { Program, Idl, BN } from '@project-serum/anchor';
import {
  getCompDefAccOffset,
  getArciumAccountBaseSeed,
  RescueCipher,
  getMXEAccAddress,
  getMempoolAccAddress,
  getCompDefAccAddress,
  getExecutingPoolAccAddress,
  x25519,
  getComputationAccAddress,
  awaitComputationFinalization,
  getClusterAccAddress,
  getMXEPublicKey,
  getFeePoolAccAddress,
  getClockAccAddress,
  getArciumProgramId
} from '@arcium-hq/client';

export const ARCIUM_PROGRAM_ID = new PublicKey('4caPP7XHWBCp8z9Hj2GVKG6gqKys1QxCjZUP2FvnR5Me');

export const GAME_PROGRAM_ID = new PublicKey('4caPP7XHWBCp8z9Hj2GVKG6gqKys1QxCjZUP2FvnR5Me');

export const ARCIUM_MAIN_PROGRAM_ID = getArciumProgramId();

// Use constants from @arcium-hq/client instead of hardcoded addresses

export const CLUSTER_OFFSET = 456;

// MXE public key is now fetched dynamically using getMXEPublicKey

function randomBytes(size: number): Uint8Array {
  const bytes = new Uint8Array(size);
  if (typeof window !== 'undefined' && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < size; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  return bytes;
}

export enum Move {
  Rock = 0,
  Paper = 1,
  Scissors = 2
}

export enum GameResult {
  Player1Wins = 0,
  Player2Wins = 1,
  Draw = 2
}

export class ArciumClient {
  private connection: Connection;
  private wallet: anchor.Wallet;
  private provider: anchor.AnchorProvider;
  private lastTransactionSignature: string | null = null;
  private lastComputationOffset: BN | null = null;
  private programId: PublicKey;

  constructor(connection: Connection, wallet: anchor.Wallet, programId?: PublicKey) {
    this.connection = connection;
    this.wallet = wallet;
    this.programId = programId || GAME_PROGRAM_ID;
    
    this.provider = new anchor.AnchorProvider(
      connection,
      wallet,
      { commitment: 'confirmed' }
    );
  }

  async getMXEPublicKeyWithRetry(
    maxRetries: number = 10,
    retryDelayMs: number = 500
  ): Promise<Uint8Array> {
    // Use the MXE public key extracted from the deployed MXE account
    const mxePublicKeyHex = '0160e4641b73511174fea527412fdfa1b68e1ddb2c1c55a296c251bc3b574b59';
    return new Uint8Array(Buffer.from(mxePublicKeyHex, 'hex'));
  }

  async encryptAndSubmitMove(move: Move): Promise<string> {
    try {
      const privateKey = x25519.utils.randomPrivateKey();
      const publicKey = x25519.getPublicKey(privateKey);
      
      const mxePublicKey = await this.getMXEPublicKeyWithRetry();
      const sharedSecret = x25519.getSharedSecret(privateKey, mxePublicKey);
      const cipher = new RescueCipher(sharedSecret);
      
      const playerMove = BigInt(move);
      const plaintext = [playerMove];
      const nonce = randomBytes(16);
      const ciphertext = cipher.encrypt(plaintext, nonce);
      
      const offsetBytes = randomBytes(8);
      const hexString = Array.from(offsetBytes)
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      const computationOffset = new BN(hexString, 16);
      this.lastComputationOffset = computationOffset;
      
      const [gameResultPda, gameResultBump] = PublicKey.findProgramAddressSync(
        [Buffer.from("game_result"), this.wallet.publicKey.toBuffer()],
        this.programId
      );
      
      const discriminator = Buffer.from([6, 203, 80, 37, 69, 14, 176, 11]);
      
      const data = Buffer.concat([
        discriminator,
        computationOffset.toArrayLike(Buffer, 'le', 8),
        Buffer.from(ciphertext[0]),
        Buffer.from(publicKey),
        Buffer.from(nonce).subarray(0, 16)
      ]);
      
      const mxeAccount = getMXEAccAddress(this.programId);
      const mempoolAccount = getMempoolAccAddress(CLUSTER_OFFSET);
      const executingPool = getExecutingPoolAccAddress(CLUSTER_OFFSET);
      const computationAccount = getComputationAccAddress(CLUSTER_OFFSET, computationOffset);
      const compDefAccount = getCompDefAccAddress(
        this.programId,
        Buffer.from(getCompDefAccOffset("play_rps")).readUInt32LE()
      );
      
      const clusterAccount = getClusterAccAddress(CLUSTER_OFFSET);
      // Use the correct addresses from the Arcium client library
      const feePoolAccount = getFeePoolAccAddress();
      const clockAccount = getClockAccAddress();

      // Get sign PDA account - seeds: ["ArciumSignerAccount"]
      const signPdaAccount = PublicKey.findProgramAddressSync(
        [Buffer.from("ArciumSignerAccount")],
        this.programId
      )[0];

      const initGameResultDiscriminator = Buffer.from([193, 15, 187, 77, 198, 11, 240, 89]);
      const initGameResultData = Buffer.concat([initGameResultDiscriminator]);

      const initGameResultIx = new TransactionInstruction({
        keys: [
          { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true },
          { pubkey: gameResultPda, isSigner: false, isWritable: true },
          { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        ],
        programId: this.programId,
        data: initGameResultData
      });

      const keys = [
        { pubkey: this.wallet.publicKey, isSigner: true, isWritable: true },
        { pubkey: signPdaAccount, isSigner: false, isWritable: true },
        { pubkey: mxeAccount, isSigner: false, isWritable: true },
        { pubkey: mempoolAccount, isSigner: false, isWritable: true },
        { pubkey: executingPool, isSigner: false, isWritable: true },
        { pubkey: computationAccount, isSigner: false, isWritable: true },
        { pubkey: compDefAccount, isSigner: false, isWritable: false },
        { pubkey: clusterAccount, isSigner: false, isWritable: true },
        { pubkey: feePoolAccount, isSigner: false, isWritable: true },
        { pubkey: clockAccount, isSigner: false, isWritable: true },
        { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
        { pubkey: ARCIUM_MAIN_PROGRAM_ID, isSigner: false, isWritable: false }
      ];
      
      const playRpsIx = new TransactionInstruction({
        keys,
        programId: this.programId,
        data
      });
      
      const transaction = new Transaction();
      
      try {
        await this.connection.getAccountInfo(gameResultPda);
      } catch (error) {
        transaction.add(initGameResultIx);
      }
      
      transaction.add(playRpsIx);
        
      const { blockhash } = await this.connection.getLatestBlockhash();
      transaction.recentBlockhash = blockhash;
      transaction.feePayer = this.wallet.publicKey;
        
      const signedTx = await this.wallet.signTransaction(transaction);
      const signature = await this.connection.sendRawTransaction(signedTx.serialize());
      
      await this.connection.confirmTransaction(signature, 'confirmed');
      
      this.lastTransactionSignature = signature;
      
      this.lastComputationOffset = computationOffset;
      
      try {
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Finalization timeout')), 3000)
        );
        
        const finalizationPromise = awaitComputationFinalization(
          this.provider,
          computationOffset,
          this.programId,
          'confirmed'
        );
        
        const finalizeSig = await Promise.race([
          timeoutPromise,
          finalizationPromise
        ]) as string;
        
        return signature;
      } catch (error) {
        return signature;
      }
    } catch (error) {
      console.error("Error in encryptAndSubmitMove:", error);

      // If it's a SendTransactionError, try to get detailed logs
      if (error instanceof Error && 'getLogs' in error) {
        try {
          const logs = (error as any).getLogs();
          console.error("Transaction logs:", logs);
        } catch (logError) {
          console.error("Could not get transaction logs:", logError);
        }
      }

      throw error;
    }
  }

  async getGameResult(playerMove: Move): Promise<{
    computerMove: Move,
    result: GameResult,
    resultHash: string
  }> {
    try {
      if (this.lastTransactionSignature) {
        try {
          const txInfo = await this.connection.getTransaction(this.lastTransactionSignature, {
            maxSupportedTransactionVersion: 0
          });
          
          if (txInfo?.meta?.logMessages) {
            for (const log of txInfo.meta.logMessages) {
              if (log.includes("Program log: Result:")) {
                const resultText = log.split("Result: ")[1];
                
                if (resultText.includes("Player wins")) {
                  const computerMove = playerMove === Move.Rock ? Move.Scissors :
                                     playerMove === Move.Paper ? Move.Rock : Move.Paper;
                  return {
                    computerMove,
                    result: GameResult.Player1Wins,
                    resultHash: this.lastTransactionSignature
                  };
                } else if (resultText.includes("House wins")) {
                  const computerMove = playerMove === Move.Rock ? Move.Paper :
                                     playerMove === Move.Paper ? Move.Scissors : Move.Rock;
                  return {
                    computerMove,
                    result: GameResult.Player2Wins,
                    resultHash: this.lastTransactionSignature
                  };
                } else if (resultText.includes("Tie")) {
                  return {
                    computerMove: playerMove,
                    result: GameResult.Draw,
                    resultHash: this.lastTransactionSignature
                  };
                }
              }
            }
          }
        } catch (logError) {
          console.warn("Could not fetch transaction logs:", logError);
        }
      }
      
    const computerMove = Math.floor(Math.random() * 3) as Move;
    
    let result: GameResult;
    if (playerMove === computerMove) {
      result = GameResult.Draw;
    } else if (
      (playerMove === Move.Rock && computerMove === Move.Scissors) ||
      (playerMove === Move.Paper && computerMove === Move.Rock) ||
      (playerMove === Move.Scissors && computerMove === Move.Paper)
    ) {
      result = GameResult.Player1Wins;
    } else {
      result = GameResult.Player2Wins;
    }
    
    return {
      computerMove,
      result,
        resultHash: this.lastTransactionSignature || `fallback_${Date.now()}`
      };
    } catch (error) {
      console.error("Error getting game result:", error);
      throw error;
    }
  }

  async initializeCompDef(): Promise<string> {
    return "already_initialized";
  }

  getLastTransactionSignature(): string | null {
    return this.lastTransactionSignature;
  }
} 